// Drawing a time clock area with the pencil (Phelipe 2026-10-09: "the pencil
// isn't drawing, it's just moving the map around"). Pencil on: hold the mouse
// down and draw around the job, and the stroke becomes the outline, trimmed to
// the corners the server accepts (at most 40); a short press is a tap, one
// corner. The pen listens on the map's own box ahead of Google Maps, so the map
// doesn't pan meanwhile; pencil off, the map behaves as usual. Shared by the
// new-project map (gc_project_form) and the project's Location box.
export const MAX_CORNERS = 40

// Meters between two spots ({lat, lng} or google LatLng).
export function metersBetween(a, b) {
  const lat = (p) => (typeof p.lat === "function" ? p.lat() : p.lat)
  const lng = (p) => (typeof p.lng === "function" ? p.lng() : p.lng)
  const r = Math.PI / 180
  const dLat = (lat(b) - lat(a)) * r, dLng = (lng(b) - lng(a)) * r
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat(a) * r) * Math.cos(lat(b) * r) * Math.sin(dLng / 2) ** 2
  return 2 * 6371000 * Math.asin(Math.sqrt(h))
}

// A tap on (or next to, about 14 px) the first corner of an outline with 3+
// corners closes the loop instead of adding a corner (Phelipe 2026-10-09:
// "closing the gap point to point", like the phone).
export function closesTheGap(corners, latLng, zoom = 18) {
  if (!corners || corners.length < 3) return false
  const first = corners[0]
  const lat = typeof first.lat === "function" ? first.lat() : first.lat
  const metersPerPixel = (156543.03 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, Number.isFinite(zoom) ? zoom : 18)
  return metersBetween(first, latLng) <= 14 * metersPerPixel
}

// Fewer points that keep the shape (Ramer–Douglas–Peucker), at most `max`.
// Points are {lat, lng}; distances are planar with longitude scaled, which is
// fine at a job's scale.
export function simplifyOutline(points, max = MAX_CORNERS) {
  const pts = points.map((p) => ({ lat: p.lat, lng: p.lng }))
  if (pts.length <= 2) return pts
  const k = Math.cos((pts[0].lat * Math.PI) / 180) || 1
  const sq = (a, b) => { const dx = (a.lng - b.lng) * k, dy = a.lat - b.lat; return dx * dx + dy * dy }
  const segSq = (p, a, b) => {
    let x = a.lng * k, y = a.lat, dx = b.lng * k - x, dy = b.lat - y
    if (dx || dy) {
      const t = ((p.lng * k - x) * dx + (p.lat - y) * dy) / (dx * dx + dy * dy)
      if (t > 1) { x = b.lng * k; y = b.lat } else if (t > 0) { x += dx * t; y += dy * t }
    }
    dx = p.lng * k - x; dy = p.lat - y
    return dx * dx + dy * dy
  }
  const rdp = (list, tolSq) => {
    if (list.length <= 2) return list
    let far = 0, at = 0
    for (let i = 1; i < list.length - 1; i++) {
      const d = segSq(list[i], list[0], list[list.length - 1])
      if (d > far) { far = d; at = i }
    }
    if (far <= tolSq) return [list[0], list[list.length - 1]]
    return rdp(list.slice(0, at + 1), tolSq).slice(0, -1).concat(rdp(list.slice(at), tolSq))
  }
  let out = pts
  let tol = 1e-7
  while (out.length > max) { out = rdp(pts, tol * tol); tol *= 1.5 }
  // A stroke that came back to its start: the outline closes itself.
  if (out.length > 3 && sq(out[0], out[out.length - 1]) <= tol * tol * 4) out = out.slice(0, -1)
  return out
}

export class FreehandPen {
  // container: the map's element; map: the google.maps.Map; onTap(latLng); onStroke([{lat, lng}]).
  constructor({ container, map, onTap, onStroke }) {
    this.container = container
    this.map = map
    this.onTap = onTap
    this.onStroke = onStroke
    this.on = false
    this.stroke = null
    const g = window.google?.maps
    if (g?.OverlayView) {
      // An empty overlay, only for its projection: pixels in the map's box → lat/lng.
      this.overlay = new g.OverlayView()
      this.overlay.onAdd = this.overlay.draw = this.overlay.onRemove = () => {}
      this.overlay.setMap(map)
    }
    this._down = (e) => this._pointerDown(e)
    this._move = (e) => this._pointerMove(e)
    this._up = (e) => this._pointerUp(e)
    this._click = (e) => { if (this.swallow) { this.swallow = false; e.stopPropagation(); e.preventDefault() } }
    for (const [name, fn] of [["pointerdown", this._down], ["pointermove", this._move], ["pointerup", this._up], ["pointercancel", this._up], ["click", this._click]]) {
      container.addEventListener(name, fn, true)
    }
  }

  enable(on) {
    this.on = !!on
    this.container.style.touchAction = this.on ? "none" : ""
    if (!this.on) { this.stroke = null; this.line?.setMap(null) }
  }

  // The line under the mouse while it's down (Phelipe 2026-10-09: "I'm not
  // seeing the line when I draw"), like the phone; the outline replaces it on release.
  _showLine(points) {
    const g = window.google?.maps
    if (!g?.Polyline) return
    if (!this.line) this.line = new g.Polyline({ clickable: false, strokeColor: "#f97316", strokeWeight: 3, strokeOpacity: 0.95 })
    this.line.setPath(points)
    if (this.line.getMap?.() !== this.map) this.line.setMap(this.map)
  }

  _latLng(e) {
    const proj = this.overlay?.getProjection?.()
    const g = window.google?.maps
    if (!proj || !g?.Point) return null
    const r = this.container.getBoundingClientRect()
    return proj.fromContainerPixelToLatLng(new g.Point(e.clientX - r.left, e.clientY - r.top))
  }

  // The zoom buttons, the pencil itself, Hybrid / Map: theirs, never a stroke.
  _isControl(e) { return !!e.target?.closest?.("button, a, select, input, [role=button]") }

  _pointerDown(e) {
    if (!this.on || this._isControl(e) || (e.button != null && e.button !== 0)) return
    const latLng = this._latLng(e)
    if (!latLng) return // no projection yet: the map keeps the click
    e.stopPropagation()
    e.preventDefault()
    this.swallow = true
    this.stroke = { points: [latLng], x: e.clientX, y: e.clientY, moved: false }
    this._showLine(this.stroke.points)
    try { this.container.setPointerCapture?.(e.pointerId) } catch { /* not every browser */ }
  }

  _pointerMove(e) {
    if (!this.stroke) return
    e.stopPropagation()
    if (Math.abs(e.clientX - this.stroke.x) + Math.abs(e.clientY - this.stroke.y) >= 4) this.stroke.moved = true
    const latLng = this._latLng(e)
    if (latLng) { this.stroke.points.push(latLng); if (this.stroke.moved) this._showLine(this.stroke.points) }
  }

  _pointerUp(e) {
    const s = this.stroke
    if (!s) return
    this.stroke = null
    this.line?.setMap(null)
    e.stopPropagation()
    e.preventDefault()
    try { this.container.releasePointerCapture?.(e.pointerId) } catch { /* not every browser */ }
    if (s.moved && s.points.length >= 6) this.onStroke(simplifyOutline(s.points.map((p) => ({ lat: p.lat(), lng: p.lng() }))))
    else this.onTap(s.points[0])
  }
}
